import { useEffect, useState } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { safeAuth, safeFrom, isSupabaseAvailable } from "@/lib/safe-supabase";
import { permissionService } from "@/services/permissionService";
import { Loader2 } from "lucide-react";

interface ProtectedRouteProps {
  children: React.ReactNode;
  requiredPermission?: string;
  requiredRole?: string;
}

export default function ProtectedRoute({ 
  children, 
  requiredPermission,
  requiredRole 
}: ProtectedRouteProps) {
  const [isLoading, setIsLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [hasPermission, setHasPermission] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const checkAuth = async () => {
      try {
        // If Supabase is not configured, deny access
        if (!isSupabaseAvailable()) {
          console.warn('[ProtectedRoute] Supabase not configured - denying admin access');
          setIsAuthenticated(false);
          setIsLoading(false);
          return;
        }

        const { session, error } = await safeAuth.getSession();
        
        if (error) {
          console.error('[ProtectedRoute] Session error:', error);
          throw error;
        }
        
        if (session) {
          // Check if user is an admin using safe database access
          const { data: adminData, error: adminError } = await safeFrom('admins')
            .select('user_id')
            .eq('user_id', session.user.id)
            .single();
            
          if (adminError || !adminData) {
            console.warn('[ProtectedRoute] User not found in admins table:', session.user.email);
            throw new Error('Not authorized as admin');
          }
          
          console.log('[ProtectedRoute] Admin access granted to:', session.user.email);
          setIsAuthenticated(true);

          // Check permissions if required
          if (requiredPermission || requiredRole) {
            try {
              if (requiredRole) {
                const hasRoleResult = await permissionService.hasRole(session.user.id, requiredRole);
                setHasPermission(hasRoleResult);
              } else if (requiredPermission) {
                const hasPermResult = await permissionService.hasPermission(session.user.id, requiredPermission);
                setHasPermission(hasPermResult);
              } else {
                setHasPermission(true);
              }
            } catch (permError) {
              // If permission check fails (e.g., roles table doesn't exist or user has no roles),
              // allow access if user is in admins table (backward compatibility)
              console.warn('[ProtectedRoute] Permission check failed, allowing access (backward compatibility):', permError);
              setHasPermission(true);
            }
          } else {
            // No permission/role required - allow access for any admin
            setHasPermission(true);
          }
        }
      } catch (error) {
        console.error('[ProtectedRoute] Auth error:', error);
        setIsAuthenticated(false);
        setHasPermission(false);
      } finally {
        setIsLoading(false);
      }
    };

    checkAuth();
  }, [requiredPermission, requiredRole]);

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/admin/login" state={{ from: location }} replace />;
  }

  if (!hasPermission) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold mb-2">Access Denied</h1>
          <p className="text-muted-foreground">
            You don't have permission to access this page.
            {requiredPermission && ` Required: ${requiredPermission}`}
            {requiredRole && ` Required Role: ${requiredRole}`}
          </p>
        </div>
      </div>
    );
  }

  return <>{children}</>;
}
